// src/components/Payment.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { paymentActions } from "../redux/paymentSlice";
import { invoiceActions } from "../redux/invoiceSlice";
import { Button, message } from "antd";
import { DollarOutlined, DownloadOutlined } from "@ant-design/icons";
import { SlArrowLeft } from "react-icons/sl";
import SyncLoader from "react-spinners/SyncLoader";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { convertToNepaliDate } from "../components/FormatDate";

const Payment = () => {
  const [isInvoice, setIsInvoice] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    invoices,
    loading: invoiceLoading,
  } = useSelector((state) => state.invoices);

  const {
    payments,
    loading: paymentLoading,
  } = useSelector((state) => state.payments);

  // -------------------------------------------------
  // Load invoices / payments
  // -------------------------------------------------
  useEffect(() => {
    if (isInvoice) {
      dispatch(invoiceActions.getInvoices());
    } else {
      dispatch(paymentActions.getPayments());
    }
  }, [isInvoice, dispatch]);

  // -------------------------------------------------
  // Helpers
  // -------------------------------------------------
  const isFullyPaid = (invoice) => {
    // status from API is reliable
    return invoice.status === "Fully Paid" || Number(invoice.remaining_amount) <= 0;
  };

  const getRemaining = (invoice) => {
    return Math.max(0, Number(invoice.remaining_amount));
  };

  const handlePayInvoice = (invoice, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isFullyPaid(invoice)) return;

    navigate("/adduserpayment", {
      state: {
        invoiceId: invoice.id,
        amount: getRemaining(invoice),           // <-- only the due
        invoiceMonth: invoice.month,
        invoiceDate: invoice.invoice_date,
      },
    });
  };

  // -------------------------------------------------
  // PDF Download (uses list data – no extra API)
  // -------------------------------------------------
  const handleDownload = async (invoice, e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      message.loading({ content: "Generating PDF...", key: "pdf" });

      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.width = "720px";
      container.style.padding = "30px";
      container.style.background = "#fff";
      container.style.fontFamily = "'Roboto', sans-serif";
      container.style.fontSize = "12px";
      document.body.appendChild(container);

      container.innerHTML = `
        <div style="max-width:660px;margin:0 auto;">
          <h2 style="text-align:center;font-size:16px;font-weight:bold;margin:0 0 4px;">
            Society of Comfort SMD Awas Bayawasthapan Samiti
          </h2>
          <p style="text-align:center;font-size:11px;margin:0 0 20px;color:#555;">
            thaiba-14, Lalitpur
          </p>

          <div style="display:flex;justify-content:space-between;font-size:11px;margin-bottom:16px;">
            <div><strong>House No:</strong> ${invoice.tenant_id}</div>
          </div>

          <div style="margin-bottom:16px;">
            <strong style="font-size:12px;">Items:</strong>
            <div style="margin-top:6px;">
              ${invoice.invoice_items
                .map(
                  (item) => `
                <div style="display:flex;justify-content:space-between;font-size:11px;padding:4px 0;border-bottom:1px dashed #ddd;">
                  <span style="flex:2;">${item.particular}</span>
                  <span style="flex:1;text-align:center;">${item.quantity}</span>
                  <span style="flex:1;text-align:right;">NPR. ${item.rate}</span>
                  <span style="flex:1;text-align:right;">NPR. ${item.quantity * item.rate}</span>
                </div>`
                )
                .join("")}
            </div>
          </div>

          <div style="display:flex;justify-content:space-between;font-size:12px;margin:12px 0;padding:6px 0;border-top:1px solid #999;">
            <strong>Total</strong>
            <span>NPR. ${invoice.total_amount}</span>
          </div>

          <div style="display:flex;justify-content:space-between;font-size:12px;margin:8px 0;padding:6px 0;border-top:1px solid #999;">
            <strong>Remaining Due</strong>
            <span>NPR. ${getRemaining(invoice)}</span>
          </div>

          <div style="font-size:11px;margin-top:12px;">
            <strong>Remarks:</strong>
            <p style="margin:4px 0;">${invoice.remarks || "No remarks"}</p>
          </div>

          <div style="margin-top:30px;text-align:center;font-size:10px;color:#777;">
            <p style="margin:2px 0;">Thank you for your payment.</p>
            <p style="margin:2px 0;">Generated on: ${new Date().toLocaleDateString("en-NP")}</p>
          </div>
        </div>
      `;

      const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: "#fff" });
      const imgWidth = 595.28;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF("p", "pt", "a4");
      pdf.addImage(canvas, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`Invoice_${invoice.id}_${invoice.month.replace(/\s+/g, "_")}.pdf`);

      document.body.removeChild(container);
      message.success({ content: "Invoice downloaded!", key: "pdf", duration: 2 });
    } catch (err) {
      console.error("PDF error:", err);
      message.error("Failed to download invoice.");
    }
  };

  // -------------------------------------------------
  // Render
  // -------------------------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 mb-14 pb-6 sm:mb-0 sm:pb-0">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#3F3F95] to-[#5858B8] px-4 py-4 w-full rounded-b-2xl mb-6 shadow-lg">
        <div className="flex items-center justify-between">
          <Link to="/userdash">
            <SlArrowLeft className="text-white text-xl hover:scale-110 transition-transform" />
          </Link>
          <h3 className="font-bold text-[22px] text-white tracking-wide">Invoice & Payment</h3>
          <div className="w-6" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex justify-around mb-6 px-4">
        <button
          className={`font-semibold flex flex-col items-center justify-center text-lg transition-all ${isInvoice ? "text-[#403F93]" : "text-gray-400"}`}
          onClick={() => setIsInvoice(true)}
        >
          Invoice List
          <span className={`h-1 w-24 rounded-full mt-2 transition-all ${isInvoice ? "bg-[#403F93]" : "bg-transparent"}`} />
        </button>
        <button
          className={`font-semibold flex flex-col items-center justify-center text-lg transition-all ${!isInvoice ? "text-[#403F93]" : "text-gray-400"}`}
          onClick={() => setIsInvoice(false)}
        >
          Payment List
          <span className={`h-1 w-24 rounded-full mt-2 transition-all ${!isInvoice ? "bg-[#403F93]" : "bg-transparent"}`} />
        </button>
      </div>

      {/* Loading */}
      {(invoiceLoading || paymentLoading) ? (
        <div className="flex justify-center mt-16">
          <SyncLoader color="#3F3F95" size={15} />
        </div>
      ) : (
        <div className="flex flex-col gap-4 px-4 pb-6">
          {/* ---------- INVOICE LIST ---------- */}
          {isInvoice &&
            invoices?.map((invoice) => {
              const remaining = getRemaining(invoice);
              const fullyPaid = isFullyPaid(invoice);

              return (
                <div
                  key={invoice.id}
                  className="relative border border-gray-200 bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow overflow-hidden"
                >
                  {/* Fully Paid Badge */}
                  {/* {fullyPaid && (
                    <div className="absolute inset-0 bg-green-50 bg-opacity-80 flex items-center justify-center z-10 rounded-2xl">
                      <span className="text-green-700 font-bold text-lg">Fully Paid</span>
                    </div>
                  )} */}

                  <Link to={`/invoice-detail/${invoice.id}`} className="block p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">
                          {convertToNepaliDate(invoice.invoice_date)}
                        </p>
                        <p className="text-sm font-medium text-gray-700">
                          Invoice for <span className="text-[#403F93] font-semibold">{invoice.month}</span>
                        </p>
                      </div>
                      <div className="bg-[#403F93] bg-opacity-10 px-3 py-1 rounded-lg">
                        <p className="text-xs text-[#403F93] font-semibold">#{invoice.id}</p>
                      </div>
                    </div>

                    <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-gray-300 to-transparent my-3" />

                    <div className="space-y-2 mb-3">
                      {invoice.invoice_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <p className="text-gray-600">{item.particular}</p>
                          <p className="font-medium text-gray-800">Rs. {item.total}</p>
                        </div>
                      ))}
                    </div>

                    <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-gray-300 to-transparent my-3" />

                    <div className="flex justify-between items-center mb-2">
                      <p className="text-base font-semibold text-gray-700">Total Amount</p>
                      <p className="text-lg font-bold text-[#403F93]">Rs. {invoice.total_amount}</p>
                    </div>

                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-600">Remaining Due</p>
                      <p className={`font-semibold ${fullyPaid ? "text-green-600" : "text-red-600"}`}>
                        Rs. {remaining}
                      </p>
                    </div>
                  </Link>

                  {/* Action Buttons */}
                  <div className="flex gap-2 px-5 pb-4">
                    <Button
                      className="flex-1 bg-gradient-to-r from-[#19891A] to-[#22A824] text-white border-0 font-medium hover:from-[#147615] hover:to-[#19891A] transition-all"
                      icon={<DownloadOutlined />}
                      style={{ height: "42px" }}
                      onClick={(e) => handleDownload(invoice, e)}
                    >
                      Download
                    </Button>

                    <Button
                      disabled={fullyPaid}
                      onClick={(e) => handlePayInvoice(invoice, e)}
                      className={`flex-1 border-0 font-medium transition-all ${
                        fullyPaid
                          ? "bg-gray-400 text-white cursor-not-allowed"
                          : "bg-gradient-to-r from-[#3F3F95] to-[#5858B8] text-white hover:from-[#353584] hover:to-[#4747A0]"
                      }`}
                      icon={<DollarOutlined />}
                      style={{ height: "42px" }}
                    >
                      {fullyPaid ? "Paid" : "Pay Invoice"}
                    </Button>
                  </div>
                </div>
              );
            })}

          {/* ---------- PAYMENT LIST ---------- */}
          {!isInvoice &&
            Array.isArray(payments) &&
            payments.map((payment) => (
              <div
                key={payment.id}
                className="border border-gray-200 bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow overflow-hidden"
              >
                <Link to={`/payment-detail/${payment.id}`} className="block p-5">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-500">Payment ID</p>
                      <p className="text-sm font-semibold text-gray-800">#{payment.id}</p>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-500">Date</p>
                      <p className="text-sm font-medium text-gray-700">{convertToNepaliDate(payment.date)}</p>
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-500">Paid Amount</p>
                      <p className="text-base font-bold text-[#403F93]">Rs. {payment.amount}</p>
                    </div>

                    <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-gray-300 to-transparent" />

                    <div className="flex justify-between items-center">
                      <p className="text-sm font-medium text-gray-600">Status</p>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          payment.status === "Confirmed"
                            ? "bg-green-100 text-green-600"
                            : payment.status === "Pending"
                            ? "bg-yellow-100 text-yellow-600"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        {payment.status}
                      </span>
                    </div>

                    {payment.status === "Rejected" && (
                      <div className="bg-red-50 p-3 rounded-lg mt-2">
                        <p className="text-xs font-semibold text-red-700 mb-1">Rejection Reason</p>
                        <p className="text-sm text-red-600">{payment.rejected_reason}</p>
                      </div>
                    )}
                  </div>
                </Link>

                {payment.status === "Pending" && (
                  <div className="px-5 pb-4">
                    <Link to={`/editpayment/${payment.id}`}>
                      <Button
                        className="w-full bg-gradient-to-r from-[#3F3F95] to-[#5858B8] text-white border-0 font-medium hover:from-[#353584] hover:to-[#4747A0] transition-all"
                        style={{ height: "42px" }}
                      >
                        Edit Payment
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default Payment;